import React, { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { PageHeader } from "@/components/Shared";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Search } from "lucide-react";
import { toast } from "sonner";

const BLANK = { title: "", first_name: "", last_name: "", gender: "M", birth_place: "", birth_date: "", fiscal_code: "",
  email: "", phone: "", address: "", vat_number: "", iban: "", notes: "" };
const FIELDS = [
  ["title", "Titolo (es. Dr., Prof.)", 1], ["first_name", "Nome *", 1], ["last_name", "Cognome *", 1],
  ["birth_place", "Luogo di nascita (es. Castelvetrano (TP))", 2], ["birth_date", "Data di nascita", 1, "date"],
  ["fiscal_code", "Codice fiscale", 2], ["vat_number", "Partita IVA", 1], ["email", "E-mail", 2], ["phone", "Telefono", 1],
  ["address", "Indirizzo", 3], ["iban", "IBAN", 3], ["notes", "Note", 3],
];

function TeacherDialog({ teacher, onClose, onSaved }) {
  const [form, setForm] = useState(BLANK);
  useEffect(() => { if (teacher) setForm({ ...BLANK, ...teacher, birth_date: teacher.birth_date || "" }); }, [teacher]);
  const save = async () => {
    try {
      const body = { ...form, birth_date: form.birth_date || null };
      if (teacher.id) await api.put(`/teachers/${teacher.id}`, body); else await api.post("/teachers", body);
      toast.success("Docente salvato"); onSaved();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={!!teacher} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl" data-testid="teacher-dialog">
        <DialogHeader><DialogTitle>{teacher?.id ? "Modifica docente" : "Nuovo docente"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-3 gap-3">
          {FIELDS.map(([k, label, span, type]) => (
            <div key={k} className={span === 3 ? "col-span-3" : span === 2 ? "col-span-3 sm:col-span-2" : "col-span-3 sm:col-span-1"}>
              <Label className="text-xs">{label}</Label>
              <Input type={type || "text"} value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`teacher-${k}`} />
            </div>
          ))}
          <div className="col-span-3 sm:col-span-1">
            <Label className="text-xs">Nella lettera</Label>
            <select value={form.gender} onChange={(e) => setForm({ ...form, gender: e.target.value })} className="w-full h-9 rounded-md border border-input px-2 text-sm" data-testid="teacher-gender">
              <option value="M">«nato a»</option><option value="F">«nata a»</option>
            </select>
          </div>
        </div>
        <DialogFooter><Button onClick={save} className="bg-[#008C45] hover:bg-[#006B34]" data-testid="teacher-save">Salva</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Teachers() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [denied, setDenied] = useState(false);
  const load = () => api.get("/teachers").then((r) => setRows(r.data)).catch(() => setDenied(true));
  useEffect(() => { load(); }, []);
  if (denied) return <p className="text-slate-500" data-testid="teachers-denied">L'archivio docenti è riservato al super admin.</p>;
  const del = async (t) => {
    if (!window.confirm(`Eliminare ${t.last_name} ${t.first_name} dall'archivio? Le lezioni manterranno il nome.`)) return;
    await api.delete(`/teachers/${t.id}`); toast.success("Docente eliminato"); load();
  };
  const shown = rows.filter((t) => `${t.last_name} ${t.first_name} ${t.fiscal_code} ${t.email}`.toLowerCase().includes(q.toLowerCase()));
  return (
    <div>
      <PageHeader title="Archivio Docenti" subtitle="Dati anagrafici, fiscali e di contatto usati per le lettere di incarico.">
        <Button onClick={() => setEditing({})} className="bg-[#0B192C] hover:bg-[#07111E]" data-testid="new-teacher-btn"><Plus size={16} className="mr-1" /> Nuovo docente</Button>
      </PageHeader>
      <div className="relative max-w-sm mb-4">
        <Search size={15} className="absolute left-3 top-2.5 text-slate-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca docente…" className="pl-9" data-testid="teacher-search" />
      </div>
      <Card className="border-slate-200 overflow-x-auto">
        <Table>
          <TableHeader className="bg-slate-50"><TableRow>
            <TableHead>Docente</TableHead><TableHead>Nascita</TableHead><TableHead>Codice fiscale</TableHead><TableHead>E-mail</TableHead><TableHead>Telefono</TableHead><TableHead />
          </TableRow></TableHeader>
          <TableBody>
            {shown.map((t) => (
              <TableRow key={t.id} data-testid={`teacher-row-${t.id}`}>
                <TableCell className="font-medium">{[t.title, t.last_name, t.first_name].filter(Boolean).join(" ")}</TableCell>
                <TableCell className="text-sm text-slate-600">{t.birth_place}{t.birth_date ? `, ${new Date(`${t.birth_date}T00:00:00`).toLocaleDateString("it-IT")}` : ""}</TableCell>
                <TableCell className="font-mono text-xs">{t.fiscal_code}</TableCell>
                <TableCell className="text-sm">{t.email}</TableCell>
                <TableCell className="text-sm">{t.phone}</TableCell>
                <TableCell className="whitespace-nowrap">
                  <Button variant="ghost" size="icon" onClick={() => setEditing(t)} data-testid={`edit-teacher-${t.id}`}><Pencil size={15} /></Button>
                  <Button variant="ghost" size="icon" onClick={() => del(t)} data-testid={`delete-teacher-${t.id}`}><Trash2 size={15} className="text-rose-500" /></Button>
                </TableCell>
              </TableRow>
            ))}
            {shown.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-slate-400 py-8">Nessun docente in archivio.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>
      <TeacherDialog teacher={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
    </div>
  );
}
